import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(current, next);
      setSuccess(true);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 grid-bg">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="font-display text-2xl font-bold tracking-widest text-neon-yellow">
            CHANGE PASSWORD
          </h1>
          <p className="text-text-secondary text-sm mt-2">
            {user?.mustChangePassword
              ? 'You must set a new password before continuing.'
              : 'Update your account password.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="cyber-panel p-8 space-y-5">
          <Input
            label="Current Password"
            type="password"
            icon={<Lock size={16} />}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
          <Input
            label="New Password"
            type="password"
            icon={<Lock size={16} />}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            hint="At least 8 characters"
            required
          />
          <Input
            label="Confirm New Password"
            type="password"
            icon={<Lock size={16} />}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 border border-neon-red/40 bg-neon-red/5 rounded text-xs text-neon-red font-mono">
              <AlertCircle size={14} className="shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-start gap-2 px-3 py-2 border border-neon-green/40 bg-neon-green/5 rounded text-xs text-neon-green font-mono">
              <CheckCircle size={14} className="shrink-0 mt-0.5" />
              <span>Password updated. Redirecting...</span>
            </div>
          )}

          <Button type="submit" loading={submitting} className="w-full">
            Update Password
          </Button>
        </form>
      </div>
    </div>
  );
}
